import { Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { AuthService } from '../../services/auth.service';
import { Gateway } from '../../interfaces/Gateway';

export type GatewayToolMode = 'username' | 'password' | 'replace';

/**
 * Shared admin modal for the gateway Tools actions that need input:
 * Change username, Change password, and Replace. `mode` selects the fields.
 */
@Component({
    selector: 'app-gateway-tool-modal',
    imports: [FormsModule, NgSelectModule],
    templateUrl: './gateway-tool-modal.component.html',
    styleUrl: './gateway-tool-modal.component.scss',
})
export class GatewayToolModalComponent implements OnInit {
    @Input() mode: GatewayToolMode = 'username';
    @Input() gateway!: Gateway;
    /** Full list, used to pick a replacement in `replace` mode. */
    @Input() gateways: Gateway[] = [];

    newUsername = '';
    newPassword = '';
    confirmPassword = '';
    replacementUid: string | null = null;
    submitting = false;
    error: string | null = null;

    /** Stable ng-select options (computed once — a getter would return a new
     *  array each change-detection pass and break selection). */
    candidateOptions: { uid: string; label: string }[] = [];

    constructor(
        public activeModal: NgbActiveModal,
        private authService: AuthService,
    ) { }

    ngOnInit(): void {
        this.candidateOptions = this.candidates.map((c) => ({
            uid: c.uid,
            label: `${c.uid} — ${c.descr}`,
        }));
    }

    get title(): string {
        return this.mode === 'username' ? 'Change username'
            : this.mode === 'password' ? 'Change password'
                : 'Replace gateway';
    }

    /** Candidate replacement gateways (everything except this one). */
    get candidates(): Gateway[] {
        return this.gateways.filter((g) => g.uid !== this.gateway?.uid);
    }

    submit(): void {
        if (this.submitting) return;
        this.error = null;

        let request$;
        if (this.mode === 'username') {
            if (!this.newUsername.trim()) return;
            request$ = this.authService.changeUsername(this.gateway.id_user!, this.newUsername.trim());
        } else if (this.mode === 'password') {
            if (!this.newPassword) return;
            if (this.newPassword !== this.confirmPassword) {
                this.error = 'Passwords do not match';
                return;
            }
            request$ = this.authService.changePassword(this.gateway.id_user!, this.newPassword);
        } else {
            const cand = this.candidates.find((c) => c.uid === this.replacementUid);
            if (!cand || !cand.code) {
                this.error = 'Please select a replacement gateway';
                return;
            }
            request$ = this.authService.replaceGateway(this.gateway.uid, cand.uid, cand.code);
        }

        this.submitting = true;
        request$.subscribe({
            next: (res) => {
                console.log(`[${this.mode}] backend response:`, res);
                this.submitting = false;
                this.activeModal.close(true);
            },
            error: (err) => {
                console.error(`[${this.mode}] backend error:`, err);
                this.submitting = false;
                this.error = 'Something went wrong. Please try again.';
            },
        });
    }
}
